import React, { useState, useEffect } from 'react';
import { CertificateTable } from '../components/CertificateTable';
import { SearchFilter } from '../components/SearchFilter';
import { MultiSelectDomainFilter } from '../components/MultiSelectDomainFilter';
import { SkeletonTable } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { certificatesAPI } from '../lib/api';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { AlertCircle, Info } from 'lucide-react';
import { STATUS } from '../lib/theme';

export function CertificatesPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [filteredCerts, setFilteredCerts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomains, setSelectedDomains] = useState([]);

  useEffect(() => {
    loadCertificates();
  }, []);

  // Filter logic
  useEffect(() => {
    if (selectedDomains.length > 0) {
      // 1. Array order exactly matches selectedDomains
      const orderedFiltered = [];
      selectedDomains.forEach(domain => {
        const matchingCerts = certificates.filter(c => c.domain === domain);
        orderedFiltered.push(...matchingCerts);
      });

      // 2. Apply search query on top of it if present
      if (searchQuery.trim() === '') {
        setFilteredCerts(orderedFiltered);
      } else {
        const query = searchQuery.toLowerCase();
        const finalFiltered = orderedFiltered.filter(cert =>
          cert.domain.toLowerCase().includes(query) ||
          cert.server_source.toLowerCase().includes(query)
        );
        setFilteredCerts(finalFiltered);
      }
    } else {
      // Normal search filter when no specific domains selected
      if (searchQuery.trim() === '') {
        setFilteredCerts(certificates);
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = certificates.filter(cert =>
          cert.domain.toLowerCase().includes(query) ||
          cert.server_source.toLowerCase().includes(query)
        );
        setFilteredCerts(filtered);
      }
    }
  }, [searchQuery, selectedDomains, certificates]);

  const loadCertificates = async () => {
    try {
      const response = await certificatesAPI.getAll();
      setCertificates(response.data);
      setFilteredCerts(response.data);
    } catch (error) {
      toast.error('Gagal memuat sertifikat');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadCertificates();
    setRefreshing(false);
    toast.success('Data sertifikat berhasil diperbarui');
  };

  const uniqueDomains = Array.from(new Set(certificates.map(c => c.domain))).sort();

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-2">
            <div className="h-5 w-48 bg-muted animate-pulse rounded-sm" />
            <div className="h-3 w-64 bg-muted animate-pulse rounded-sm" />
          </div>
          <div className="h-9 w-32 bg-muted animate-pulse rounded-sm" />
        </div>
        <div className="space-y-2">
          <div className="h-14 w-full bg-muted animate-pulse rounded-sm" />
          <div className="h-14 w-full bg-muted animate-pulse rounded-sm" />
        </div>
        <div className="h-9 w-full bg-muted animate-pulse rounded-sm" />
        <SkeletonTable />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Breadcrumb + Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <span>Manajemen</span>
            <span>›</span>
            <span style={{ color: STATUS.primary.text }} className="font-medium">Sertifikat SSL</span>
          </div>
          <h2 className="text-base sm:text-lg font-semibold tracking-tight">Sertifikat SSL</h2>
          <p className="text-muted-foreground mt-0.5 text-xs">
            Kelola dan monitor semua sertifikat SSL
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          className="w-full sm:w-auto"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Segarkan
        </Button>
      </div>

      {/* Important Notices */}
      <div className="space-y-2">
        <Alert style={{ background: STATUS.warning.bg, borderColor: STATUS.warning.border }}>
          <AlertCircle className="h-4 w-4" style={{ color: STATUS.warning.icon }} />
          <AlertTitle style={{ color: STATUS.warning.text }}>Perhatian Rate Limit</AlertTitle>
          <AlertDescription style={{ color: STATUS.warning.text }}>
            Let's Encrypt membatasi pembaruan. Jika muncul error rate limit, tunggu 1-2 jam sebelum mencoba lagi.
          </AlertDescription>
        </Alert>

        <Alert style={{ background: STATUS.primary.bg, borderColor: STATUS.primary.border }}>
          <Info className="h-4 w-4" style={{ color: STATUS.primary.icon }} />
          <AlertTitle style={{ color: STATUS.primary.text }}>Tips Pembaruan</AlertTitle>
          <AlertDescription style={{ color: STATUS.primary.text }}>
            Selalu lakukan <strong>Uji Pembaruan</strong> terlebih dahulu sebelum <strong>Pembaruan Live</strong> untuk memastikan tidak ada masalah.
          </AlertDescription>
        </Alert>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:flex-1 items-start sm:items-center">
          <div className="w-full sm:w-72">
            <SearchFilter
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Cari domain atau server..."
            />
          </div>
          <MultiSelectDomainFilter
            domains={uniqueDomains}
            selectedDomains={selectedDomains}
            onChange={setSelectedDomains}
          />
        </div>
        <div className="text-xs font-medium text-muted-foreground whitespace-nowrap bg-muted/50 px-3 py-1.5 rounded-sm">
          {filteredCerts.length} dari {certificates.length} sertifikat
        </div>
      </div>

      {/* Certificates Table */}
      <CertificateTable
        certificates={filteredCerts}
        onRefresh={handleRefresh}
        isCustomOrdered={selectedDomains.length > 0}
      />
    </div>
  );
}